import { Notification } from 'modules/core/domain/interfaces/supabase/notifications/types';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useIsAdmin } from 'shared/hooks/use-is-admin';

import { AppDispatch } from 'store/store';
import Breadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { updateNotification } from 'modules/notifications/store/actions';
import NotificationForm from '../components/notification-form';
import NotificationFormSkeleton from '../components/notification-form-skeleton';
import { useNotification } from '../hooks/use-notification';

const EditNotificationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isAdmin = useIsAdmin();
  const { notification, loading, error } = useNotification(Number(id));
  const { setHeaderContent } = useMainLayout();

  const handleSubmit = useCallback(
    async (values: Partial<Notification>) => {
      if (!id) {
        return;
      }
      try {
        await dispatch(updateNotification({ ...values, id: Number(id) })).unwrap();
        toast.success('Уведомление обновлено');
        navigate(Pages.NOTIFICATIONS.INDEX);
      } catch (error) {
        console.error('Error updating notification:', error);
        toast.error('Ошибка обновления уведомления');
      }
    },
    [dispatch, navigate, id],
  );

  const handleCancel = useCallback(() => {
    navigate(Pages.NOTIFICATIONS.INDEX);
  }, [navigate]);

  const breadcrumbs = useMemo(() => {
    return [
      { label: 'Уведомления', to: Pages.NOTIFICATIONS.INDEX },
      {
        label: `Редактирование уведомления: ${notification?.title}`,
        to: Pages.NOTIFICATIONS.builders.edit(Number(id)),
      },
    ];
  }, [id, notification?.title]);

  useEffect(() => {
    setHeaderContent(<Breadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  if (!isAdmin) {
    return <Navigate to={Pages.NOTIFICATIONS.INDEX} replace />;
  }

  if (error !== null) {
    return (
      <>
        <div className="w-full p-6 px-6" style={{ margin: '0 auto' }}>
          <div className="alert alert-danger">Ошибка загрузки уведомления: {error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-full p-6 px-6" style={{ margin: '0 auto' }}>
        {loading ? (
          <NotificationFormSkeleton />
        ) : (
          notification && (
            <NotificationForm initialValues={notification} onSubmit={handleSubmit} onCancel={handleCancel} loading={loading} />
          )
        )}
      </div>
    </>
  );
};

export default EditNotificationPage;
